import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router";
import { post, put } from "@common/api/apiShared";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useValidator } from "@common/hooks/validation";
import { useTranslation } from "@localization/useTranslation";
import { useGroupEntitiesQuery } from "../../processingActivity/hooks/useGroupEntitiesQuery";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../useUrlProvider";
import { EntityType } from "@common/Common.types";
import { RelationViewModel } from "../../taskManagement/createTaskPane/CreateTaskPane.types";
import { InternalAssessmentGroupSaveModel } from "../InternalAssessmentGroups.types";
import { ICreateInternalAssessmentGroupDialog } from "./CreateInternalAssessmentGroupDialog";

export default function useCreateInternalAssessmentGroupDialog(props: ICreateInternalAssessmentGroupDialog) {
    const snackbar = useDotLegalSnackbar();
    const navigate = useNavigate();
    const { gdpo } = useUserContext();
    const { translateString, translateDate } = useTranslation();
    const { getInternalAssessmentGroupDraftUrl } = useUrlProvider();

    const isEdit = props.internalAssessmentGroup !== undefined;

    const [model, setModel] = useState<InternalAssessmentGroupSaveModel>(getInitialModel());
    const [step, setStep] = useState<"formData" | "selectGroupEntity">("formData");

    const groupEntitiesQuery = useGroupEntitiesQuery();
    const responsibleQuery = useResponsibleQuery(true);

    const selectableGroupEntitiesData = groupEntitiesQuery.allGroupEntities;
    const showSelectGroupEntityStep = !props.internalAssessmentGroupInProgress && (selectableGroupEntitiesData?.length ?? 0) > 1;

    const validation = useValidator<InternalAssessmentGroupSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            questionnaireId: validators.validateNotEmpty(
                (item) => item.questionnaireId,
                translateString("assessmentTemplate"),
                () => props.internalAssessmentGroupInProgress === true
            ),
            responsibleId: validators.validateNotEmpty((item) => item.responsibleId, translateString("responsible")),
            deadline: validators.validateTodayOrInTheFuture(
                (item) => item.deadline,
                translateString("deadline"),
                (item) => isEdit && deadlineIsUnchanged(item)
            ),
            groupEntityId: validators.validateNotEmpty(
                (item) => item.groupEntityId,
                translateString("groupEntity"),
                () => gdpo || step !== "selectGroupEntity"
            ),
        }),
        [step]
    );

    validation.check(model);

    const createMutation = useDotLegalMutation((saveModel: InternalAssessmentGroupSaveModel) => post<{ id: string }>("/internalAssessmentGroups", saveModel), {
        onSuccess: (response: any) => {
            snackbar.show(translateString("internalAssessmentGroupCreated"));
            navigate(getInternalAssessmentGroupDraftUrl(response.value().id));
        },
    });

    const updateMutation = useDotLegalMutation(
        (saveModel: InternalAssessmentGroupSaveModel) => put<{}>(`/internalAssessmentGroups/${props.internalAssessmentGroupId}`, saveModel),
        {
            onSuccess: () => {
                snackbar.show(createElement(Trans, { i18nKey: "itemEditedSuccess", values: { name: model.name } }));
                props.onInternalAssessmentGroupUpdated?.();
            },
        }
    );

    const onNameChange = (name: string) => setModel({ ...model, name });

    const onDescriptionChange = (description: string, mentionedEntities: Array<RelationViewModel>) =>
        setModel({
            ...model,
            description,
            relations: mentionedEntities.map((mentioned) => ({
                fromEntityId: props.internalAssessmentGroupId,
                fromEntityType: EntityType.InternalAssessmentGroup,
                toEntityType: mentioned.entityType,
                toEntityId: mentioned.entityId,
            })),
        });

    const onResponsibleChange = (responsibleId: string | null) => setModel({ ...model, responsibleId: responsibleId ?? undefined });
    const onDeadlineChange = (deadline: Date | null) => setModel({ ...model, deadline: deadline ?? undefined });

    const onQuestionnaireChange = (questionnaireId: string | null, questionnaireName?: string) =>
        setModel({ ...model, questionnaireId: questionnaireId ?? undefined, questionnaireName });

    const onGroupEntityChanged = (groupEntityIds: Array<string>) => {
        if (groupEntityIds.length === 0) {
            setModel({ ...model, groupEntityId: undefined });
        } else {
            setModel({ ...model, groupEntityId: groupEntityIds[groupEntityIds.length - 1] });
        }
    };

    const onNextOrSaveClick = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (step === "formData" && showSelectGroupEntityStep) {
            setStep("selectGroupEntity");
            return;
        }

        save();
    };

    const onPreviousClick = step === "selectGroupEntity" ? () => setStep("formData") : undefined;

    function save() {
        if (isEdit) {
            updateMutation.mutate(model);
        } else {
            createMutation.mutate(model);
        }
    }

    function getDefaultModel(): InternalAssessmentGroupSaveModel {
        return { name: translateString("defaultInternalAssessmentGroupName", { date: translateDate(new Date()) }), description: "", relations: [] };
    }

    function getInitialModel(): InternalAssessmentGroupSaveModel {
        if (!props.internalAssessmentGroup) {
            return getDefaultModel();
        }

        // The overview hands us the deadline straight off the API row, where it is still a JSON string despite
        // its Date type. The date validator compares it against a Date, so it has to be a real Date by then.
        const { deadline } = props.internalAssessmentGroup;
        return { ...props.internalAssessmentGroup, deadline: deadline ? new Date(deadline) : undefined };
    }

    function deadlineIsUnchanged(item: InternalAssessmentGroupSaveModel) {
        const originalDeadline = props.internalAssessmentGroup?.deadline;
        return originalDeadline !== undefined && item.deadline?.toDateString() === new Date(originalDeadline).toDateString();
    }

    return {
        model,
        step,
        isEdit,
        validation,
        selectableResponsiblesData: responsibleQuery.data(undefined) ?? [],
        selectableResponsiblesLoading: responsibleQuery.isLoading,
        selectableGroupEntitiesData,
        selectableGroupEntitiesLoading: groupEntitiesQuery.isLoading,
        showSelectGroupEntityStep,
        inProgress: createMutation.isLoading || updateMutation.isLoading,
        onNameChange,
        onDescriptionChange,
        onResponsibleChange,
        onDeadlineChange,
        onQuestionnaireChange,
        onGroupEntityChanged,
        onNextOrSaveClick,
        onPreviousClick,
    };
}
